<template>
  <div id="printable">
    <div v-if="isDataLoading">
      {{ $t('pages.loading') }}
    </div>
    <v-table
      v-if="$route.query.view === 'table' && !isDataLoading"
      :row-active-id="rowActiveId"
      :data="filteredScreenshots"
      :columns="columns"
      clickable
      @row-click="onRowClick"
      @row-dblclick="openModal"
    >
      <template v-slot:column-time="item">
        <p
          v-if="item.row.isNew"
          :class="$style.new"
        >
          NEW
        </p>
        {{ item.row.date | dateTime }}
      </template>
      <template v-slot:column-caption="item">
        <span :class="$style.dangerous">
          <dangerous :value="item.row.statusDangerous" />
        </span>
        {{ item.row.caption }}
      </template>
    </v-table>
    <view-grid
      v-if="isViewGrid && !isDataLoading"
      :screenshots="filteredScreenshots"
    />
    <additional-list-image
      v-if="rowActiveId"
      :list="additionalList"
      :image="additionalImage"
      @image-dblclick="openModal"
    />
    <screenshots-modal
      v-if="isShowModal"
      :screenshots="filteredScreenshots"
    />
  </div>
</template>

<script>
  import VTable from '@/components/common/table/table.vue';
  import AdditionalListImage from '@/components/common/logs/additional/list-image/index.vue';
  import ScreenshotsModal from '@/components/pages/computers/log/screenshots/components-default/modal.vue';
  import ViewGrid from '@/components/pages/computers/log/screenshots/components-default/view-grid.vue';
  import { dateTime } from '@/helpers/filters';
  import Dangerous from '@/components/common/dangerous/index.vue';
  import { getScreenshotUrl } from '@/helpers/methods';
  import { LogData } from '@/domains/log/data';

  import workerBasedRequestMixin from '@/components/common/logs/mixins/workerBasedRequestMixin';

  export default {
    components: {
      VTable,
      AdditionalListImage,
      ScreenshotsModal,
      ViewGrid,
      Dangerous,
    },
    filters: {
      dateTime,
    },
    mixins: [workerBasedRequestMixin],
    data() {
      return {
        columns: [
          {
            field: 'time',
            title: this.$t('pages.columns.time'),
            sortFn: (isAsc) => (a, b) => (new Date(a.date) - new Date(b.date)) * (isAsc ? 1 : -1),
          },
          { field: 'caption', title: this.$t('pages.columns.caption') },
          { field: 'username', title: this.$t('pages.columns.username') },
        ],
      };
    },
    computed: {
      isViewGrid() {
        return this.$route.query.view === 'grid';
      },
      isAsc() {
        return this.$route.query.sort === 'asc';
      },
      rowActiveId() {
        return parseInt(this.$route.params.record, 10);
      },
      isShowModal() {
        return Object.prototype.hasOwnProperty.call(this.$route.query, 'modal');
      },
      filteredScreenshots() {
        const items = (this.pagedData || []).filter((el) => el !== undefined);
        if (!items.length) return [];

        const filtered = LogData.filterTable(
          items,
          [(item) => dateTime(item.date), 'caption', 'username'],
          this.$route.query.filter,
        );

        return [...filtered].sort(
          (a, b) => (new Date(a.date) - new Date(b.date)) * (this.isAsc && this.isViewGrid ? 1 : -1),
        );
      },
      activeRow() {
        return this.pagedData.find((i) => i?.id === this.rowActiveId) || {};
      },
      additionalList() {
        const row = this.activeRow;
        return [
          [this.$t('pages.columns.time'), dateTime(row.date)],
          [this.$t('pages.columns.caption'), row.caption],
          [this.$t('pages.columns.path'), row.path],
        ];
      },
      additionalImage() {
        return getScreenshotUrl.call(this, this.activeRow.path);
      },
    },
    watch: {
      '$route.query.dateFrom': function (value, oldValue) {
        if (value !== oldValue) this.fetch();
      },
      '$route.query.dateTo': function (value, oldValue) {
        if (value !== oldValue) this.fetch();
      },
    },
    async created() {
      this.$store.commit('logs/printData/setColumns', this.columns);

      await this.fetch();
      if (!this.$route.query.view) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            view: 'table',
          },
        });
      }
    },
    methods: {
      onRowClick(id) {
        this.$router.push({
          name: 'computers-computer-log-record',
          params: { record: id },
          query: this.$route.query,
        });
      },
      openModal() {
        this.$router.replace({
          query: {
            ...this.$route.query,
            modal: null,
          },
        });
      },
    },
  };
</script>

<style lang="scss" module>
  .new {
    display: inline-block;
    background: $light-blue;
    color: $blue;
    font-size: 7px;
    padding: 3px;
    margin-right: 8px;
    border-radius: 1px;
  }

  .dangerous {
    display: inline-block;
    margin-right: 8px;
  }
</style>
